import {
Component,
Inject,
Input,
OnDestroy,
OnInit
} from '@angular/core';
import { FormControl,ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog,MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Router } from '@angular/router';
import { firstValueFrom,merge,of,Subject } from 'rxjs';
import {
catchError,
debounceTime,
distinctUntilChanged,
map,
startWith,
switchMap,
takeUntil,
} from 'rxjs/operators';
import { ConfirmDialog } from '../../dialogs/confirm.dialog';
import {
LogServiceToken,
RepositoryServiceToken
} from '../../services';
import { ILogService,IRepositoryService } from '../../services/i';
import { IDWViewModel as IdwUser,UserViewModel } from '../../view-models';
import { IdwFilterViewModel } from '../../view-models/filters';
import { HeaderComponent } from '../header/header.component';
@Component({
  selector: 'app-add-user',
  templateUrl: './add-user.component.html',
  styleUrls: ['./add-user.component.css'],
  imports: [
    HeaderComponent,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatToolbarModule,
    MatListModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  standalone: true,
})
export class AddUserComponent implements OnInit, OnDestroy {
  users: IdwUser[] = [];
  loading = true;
  loadError = '';
  addError = '';
  addingHawkId = '';
  @Input() keywords: FormControl = new FormControl('');
  dialogRef: MatDialogRef<ConfirmDialog>;
  private readonly retry$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  // http://blog.angular-university.io/introduction-to-angular-2-forms-template-driven-vs-model-driven/
  // Regarding subscribe and emit: https://scotch.io/tutorials/angular-2-http-requests-with-observables
  // Automapper (if really necessary): https://github.com/loedeman/AutoMapper/wiki
  constructor(
    @Inject(RepositoryServiceToken) private repo: IRepositoryService,
    @Inject(LogServiceToken) private log: ILogService,
    public dialog: MatDialog,
    private router: Router
  ) {}

  openDialog(message: string): Promise<any> {
    this.dialogRef = this.dialog.open(ConfirmDialog, {
      disableClose: false,
    });
    this.dialogRef.componentInstance.title = 'Confirm Add User';
    this.dialogRef.componentInstance.message = message;

    this.dialogRef.afterClosed().subscribe(result => {
      this.dialogRef = null;
    });
    return this.dialogRef.afterClosed().toPromise();
  }

  async addUser(user: IdwUser) {
    if (this.addingHawkId) {
      return;
    }

    this.addError = '';
    this.addingHawkId = user.HawkId;
    try {
      const confirm = await this.openDialog(`Add user ${user.First} ${user.Last}?`);
      if (confirm) {
        const uvm = new UserViewModel();
        uvm.HawkId = user.HawkId;
        uvm.Email = user.Email;
        uvm.First = user.First;
        uvm.Middle = user.Middle;
        uvm.Last = user.Last;
        uvm.Title = user.Title;
        uvm.Department = user.Department;
        const createdUser = await firstValueFrom(
          this.repo.Create(UserViewModel, uvm)
        );
        await this.router.navigate(['/users', createdUser.Id]);
      }
    } catch (error) {
      this.addError = this.getAddUserError(error, user);
      this.log.warn(`Unknown error adding user: ${user.HawkId}`, error);
    } finally {
      this.addingHawkId = '';
    }
  }

  clearSearch() {
    this.keywords.setValue('', { emitEvent: false });
    this.retry$.next();
  }

  retrySearch() {
    this.retry$.next();
  }

  get hasSearch(): boolean {
    return !!String(this.keywords.value || '').trim();
  }

  private getAddUserError(error: unknown, user: IdwUser): string {
    const httpError = error as {
      status?: number;
      error?: string | { message?: string; Message?: string };
      message?: string;
    };
    const responseMessage =
      typeof httpError?.error === 'string'
        ? httpError.error
        : httpError?.error?.message || httpError?.error?.Message || '';
    const messages = [responseMessage, httpError?.message || ''].filter(
      message => !!message
    );
    const alreadyExistsMessage = messages.find(message =>
      /already exists/i.test(message)
    );
    const isBadRequest =
      httpError?.status === 400 || messages.some(message => /\b400\b/.test(message));

    if (isBadRequest && alreadyExistsMessage) {
      return alreadyExistsMessage;
    }

    return `Unable to add ${user.First} ${user.Last}. Please try again.`;
  }

  ngOnInit() {
    const searchChanges$ = this.keywords.valueChanges.pipe(
      startWith(this.keywords.value || ''),
      debounceTime(400),
      distinctUntilChanged(),
      map(value => String(value || ''))
    );
    const retries$ = this.retry$.pipe(
      map(() => String(this.keywords.value || ''))
    );

    merge(searchChanges$, retries$)
      .pipe(
        switchMap(search => {
          const filter = new IdwFilterViewModel();
          const trimmedSearch = search.trim();
          filter.Keywords = trimmedSearch ? trimmedSearch.split(/\s+/) : [];
          this.loading = true;
          this.loadError = '';

          return this.repo.Page(IdwUser, filter).pipe(
            map(users => ({ users, error: null })),
            catchError(error => of({ users: null, error }))
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        this.loading = false;
        if (result.error) {
          this.loadError = 'Unable to search the user directory. Please try again.';
          this.log.warn('Unable to search the user directory.', result.error);
          return;
        }
        this.users = result.users || [];
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
